import React from 'react';
import { Box, Typography, IconButton, Button, Divider, Stack } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { PaymentConcept } from '@/types/payment.types'

interface PaymentSummaryProps {
  items: PaymentConcept[];
  total: number;
  onRemoveItem: (id: string, studentId?: string) => void;
  handlePay: () => void
}

const PurchaseSummary: React.FC<PaymentSummaryProps> = ({ items, total, onRemoveItem, handlePay }) => {
  return (
    <Box sx={{
      mx: { xs: 0, md: 2, lg:4 },
      minHeight: { xs: "300px", md: "350px", lg: "400px" }
    }}>
      {/* Table Headers */}
      {items.length > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            pb: { xs: 1, md: 1.5 },
            borderBottom: '2px solid',
            borderColor: 'divider',
            mb: 1
          }}
        >
          <Box sx={{ width: { xs: 28, md: 36 } }} />
          <Typography
            variant="body2"
            fontWeight={600}
            color="text.secondary"
            sx={{
              flex: 1,
              fontSize: { xs: "0.75rem", md: "0.875rem" }
            }}
          >
            Concepto
          </Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            color="text.secondary"
            sx={{
              flex: 1,
              fontSize: { xs: "0.75rem", md: "0.875rem" }
            }}
          >
            Estudiante
          </Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            color="text.secondary"
            sx={{
              width: { xs: 25, md: 30 },
              textAlign: 'center',
              fontSize: { xs: "0.75rem", md: "0.875rem" }
            }}
          >
            Cant.
          </Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            color="text.secondary"
            sx={{
              width: { xs: 70, md: 80 },
              textAlign: 'right',
              fontSize: { xs: "0.75rem", md: "0.875rem" }
            }}
          >
            Valor
          </Typography>
        </Stack>
      )}

      {/* Items List */}
      <Box sx={{
        mb: 3,
        height: { xs: "200px", md: "250px", lg: "300px" },
        overflowY: 'auto'
      }}>
        {items.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              py: 3,
              textAlign: 'center',
              fontSize: { xs: "0.875rem", md: "1rem" }
            }}
          >
            No hay ítems agregados
          </Typography>
        ) : (
          items.map((item, index) => (
            <Stack
              key={`${item.id}-${item.studentId || 'no-student'}-${index}`}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                py: { xs: 1, md: 1.5 }
              }}
            >
              <IconButton
                size="small"
                onClick={() => onRemoveItem(item.id, item.studentId)}
                sx={{ p: { xs: 0.5, md: 1 } }}
              >
                <Delete sx={{ fontSize: { xs: 18, md: 20 } }} />
              </IconButton>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{
                  flex: 1,
                  fontSize: { xs: "0.8rem", md: "0.875rem" }
                }}
              >
                {item.name}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{
                  flex: 1,
                  fontSize: { xs: "0.8rem", md: "0.875rem" }
                }}
              >
                {item.studentName || item.studentId || 'N/A'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  width: { xs: 25, md: 30 },
                  textAlign: 'center',
                  fontSize: { xs: "0.8rem", md: "0.875rem" }
                }}
              >
                {item.cant}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  width: { xs: 70, md: 80 },
                  textAlign: 'right',
                  fontSize: { xs: "0.8rem", md: "0.875rem" }
                }}
              >
                $ {item.amount * item.cant}
              </Typography>

            </Stack>
          ))
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Stack
        direction="row"
        justifyContent="end"
        sx={{ mb: 3 }}
      >
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            mx: { xs: 2, md: 4, lg: 8 },
            fontSize: { xs: "1rem", md: "1.125rem", lg: "1.25rem" }
          }}
        >
          TOTAL
        </Typography>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ fontSize: { xs: "1rem", md: "1.125rem", lg: "1.25rem" } }}
        >
          $ {total.toLocaleString()}
        </Typography>
      </Stack>

      <Box sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-end"
      }}>
        <Button
          variant="contained"
          fullWidth
          disabled={items.length === 0}
          onClick={handlePay}
          sx={{
            width: { xs: "100%", md: 250, lg: 230 },
            color: 'white',
            fontWeight: 600,
            backgroundColor: 'primary.main',
            '&:hover': { backgroundColor: 'primary.500' },
            textTransform: 'none',
            py: { xs: 1.2, md: 1.5 },
            fontSize: { xs: "0.875rem", md: "1rem" }
          }}
        >
          Iniciar PAGO
        </Button>
      </Box>
    </Box>
  );
};

export default PurchaseSummary;